import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar, Shield, Building, Phone, FileText, DollarSign, Gauge } from 'lucide-react';

const PROTECTION_TYPES = [
  { value: 'registration', label: 'Registration', description: 'Vehicle registration renewal' },
  { value: 'insurance', label: 'Insurance', description: 'Auto insurance coverage' },
  { value: 'warranty', label: 'Warranty', description: 'Manufacturer warranty' },
  { value: 'extended_warranty', label: 'Extended Warranty', description: 'Extended protection plan' },
  { value: 'service_contract', label: 'Service Contract', description: 'Prepaid maintenance plan' },
];

const AddProtectionDialog = ({ 
  open, 
  onClose, 
  vehicleId,
  initialType,
  onSubmit 
}) => {
  const [formData, setFormData] = useState({
    protection_type: initialType || 'registration',
    provider: '',
    policy_number: '',
    coverage_type: '',
    start_date: '',
    expiration_date: '',
    expiration_mileage: '',
    claim_phone: '',
    agent_name: '',
    agent_phone: '',
    deductible: '',
    cost: '',
    notes: '',
  });

  useEffect(() => {
    if (initialType) {
      setFormData(prev => ({ ...prev, protection_type: initialType }));
    }
  }, [initialType]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    onSubmit({
      ...formData,
      expiration_mileage: formData.expiration_mileage ? parseInt(formData.expiration_mileage) : null,
      deductible: formData.deductible ? parseFloat(formData.deductible) : null,
      cost: formData.cost ? parseFloat(formData.cost) : null,
    });
  };

  const selectedType = PROTECTION_TYPES.find(t => t.value === formData.protection_type);

  // Different fields based on protection type
  const showInsuranceFields = ['insurance'].includes(formData.protection_type);
  const showWarrantyFields = ['warranty', 'extended_warranty', 'service_contract'].includes(formData.protection_type);
  const showMileageExpiration = ['warranty', 'extended_warranty', 'service_contract'].includes(formData.protection_type);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
              <Shield className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <DialogTitle className="text-xl">Add Protection</DialogTitle>
              <p className="text-sm text-slate-500">Track your vehicle coverage</p>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          {/* Protection Type */}
          <div>
            <Label htmlFor="protection_type" className="flex items-center gap-2">
              <Shield className="h-4 w-4 text-teal-600" />
              Protection Type
            </Label>
            <Select
              value={formData.protection_type}
              onValueChange={(value) => handleChange('protection_type', value)}
            >
              <SelectTrigger className="mt-1" data-testid="protection-type-select">
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                {PROTECTION_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <span>{type.label}</span>
                      <span className="text-xs text-slate-400 ml-2">{type.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Provider */}
          <div>
            <Label htmlFor="provider" className="flex items-center gap-2">
              <Building className="h-4 w-4 text-teal-600" />
              Provider / Company
            </Label>
            <Input
              id="provider"
              type="text"
              placeholder={showInsuranceFields ? "Insurance company name" : "Provider name"}
              value={formData.provider}
              onChange={(e) => handleChange('provider', e.target.value)}
              className="mt-1"
              data-testid="provider-input"
            />
          </div>

          {/* Policy/Coverage Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="policy_number">Policy Number</Label>
              <Input
                id="policy_number"
                type="text"
                placeholder="Policy #"
                value={formData.policy_number}
                onChange={(e) => handleChange('policy_number', e.target.value)}
                className="mt-1"
                data-testid="policy-number-input"
              />
            </div>
            {showWarrantyFields && (
              <div>
                <Label htmlFor="coverage_type">Coverage Type</Label>
                <Input
                  id="coverage_type"
                  type="text"
                  placeholder="e.g., Bumper to Bumper"
                  value={formData.coverage_type}
                  onChange={(e) => handleChange('coverage_type', e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-teal-600" />
                Start Date
              </Label>
              <Input
                id="start_date"
                type="date"
                value={formData.start_date}
                onChange={(e) => handleChange('start_date', e.target.value)}
                className="mt-1"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <Label htmlFor="expiration_date" className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-teal-600" />
                Expiration Date
              </Label>
              <Input
                id="expiration_date"
                type="date"
                value={formData.expiration_date}
                onChange={(e) => handleChange('expiration_date', e.target.value)}
                className="mt-1"
                required
                data-testid="expiration-date-input"
              />
            </div>
          </div>

          {/* Mileage Expiration (for warranties) */}
          {showMileageExpiration && (
            <div>
              <Label htmlFor="expiration_mileage" className="flex items-center gap-2">
                <Gauge className="h-4 w-4 text-teal-600" />
                Expiration Mileage (optional)
              </Label>
              <Input
                id="expiration_mileage"
                type="number"
                placeholder="e.g., 60000"
                value={formData.expiration_mileage}
                onChange={(e) => handleChange('expiration_mileage', e.target.value)}
                className="mt-1"
                data-testid="expiration-mileage-input"
              />
            </div>
          )}

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="claim_phone" className="flex items-center gap-2">
                <Phone className="h-4 w-4 text-teal-600" />
                Claims Phone
              </Label>
              <Input
                id="claim_phone"
                type="tel"
                placeholder="1-800-XXX-XXXX"
                value={formData.claim_phone}
                onChange={(e) => handleChange('claim_phone', e.target.value)}
                className="mt-1"
                data-testid="claim-phone-input"
              />
            </div>
            {showInsuranceFields && (
              <div>
                <Label htmlFor="agent_phone">Agent Phone</Label>
                <Input
                  id="agent_phone"
                  type="tel"
                  placeholder="Agent's phone"
                  value={formData.agent_phone}
                  onChange={(e) => handleChange('agent_phone', e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* Insurance-specific fields */}
          {showInsuranceFields && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="agent_name">Agent Name</Label>
                <Input
                  id="agent_name"
                  type="text"
                  placeholder="Your agent's name"
                  value={formData.agent_name}
                  onChange={(e) => handleChange('agent_name', e.target.value)}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="deductible" className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-teal-600" />
                  Deductible
                </Label>
                <Input
                  id="deductible"
                  type="number"
                  step="0.01"
                  placeholder="500"
                  value={formData.deductible}
                  onChange={(e) => handleChange('deductible', e.target.value)}
                  className="mt-1"
                />
              </div>
            </div>
          )}

          {/* Cost */}
          <div>
            <Label htmlFor="cost" className="flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-teal-600" />
              Cost / Premium (optional)
            </Label>
            <Input
              id="cost"
              type="number"
              step="0.01"
              placeholder="Annual or total cost"
              value={formData.cost}
              onChange={(e) => handleChange('cost', e.target.value)}
              className="mt-1"
              data-testid="cost-input"
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional details about the coverage..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              className="mt-1"
              rows={2}
              data-testid="protection-notes-input"
            />
          </div>
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            type="submit" 
            onClick={handleSubmit}
            className="bg-purple-600 hover:bg-purple-700"
            data-testid="submit-protection-btn"
          >
            <Shield className="h-4 w-4 mr-2" />
            Add {selectedType?.label || 'Protection'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddProtectionDialog;
